import React, { useState, useRef, useEffect } from 'react';
import { Upload, Camera, Sparkles, ChevronLeft, ChevronRight, Moon, Sun, ArrowLeft, Download, RefreshCw, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { FilesetResolver, FaceLandmarker } from '@mediapipe/tasks-vision';

/* ── Global MediaPipe FaceLandmarker Singleton ── */
let faceLandmarkerPromise = null;
const getFaceLandmarker = () => {
    if (!faceLandmarkerPromise) {
        faceLandmarkerPromise = (async () => {
            try {
                const filesetResolver = await FilesetResolver.forVisionTasks(
                    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
                );
                return await FaceLandmarker.createFromOptions(filesetResolver, {
                    baseOptions: {
                        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
                        delegate: "GPU"
                    },
                    runningMode: "IMAGE",
                    numFaces: 1
                });
            } catch (err) {
                console.warn("GPU FaceLandmarker init failed, falling back to CPU:", err);
                const filesetResolver = await FilesetResolver.forVisionTasks(
                    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
                );
                return await FaceLandmarker.createFromOptions(filesetResolver, {
                    baseOptions: {
                        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
                        delegate: "CPU"
                    },
                    runningMode: "IMAGE",
                    numFaces: 1
                });
            }
        })();
    }
    return faceLandmarkerPromise;
};

/* ── Automatic Intraoral Landmark & Crop Extractor ── */
const autoDetectMouthAndMask = async (sourceImageBase64) => {
    return new Promise(async (resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = async () => {
            const width = img.width;
            const height = img.height;
            let cropX, cropY, cropWidth, cropHeight;
            let innerLipPointsInCrop = [];

            try {
                const landmarker = await getFaceLandmarker();
                const detections = landmarker.detect(img);

                if (detections && detections.faceLandmarks && detections.faceLandmarks.length > 0) {
                    const landmarks = detections.faceLandmarks[0];

                    // Inner lip landmark loop:
                    // 78 (left corner), 191, 80, 81, 82, 13 (upper center), 312, 311, 310, 415, 308 (right corner), 
                    // 324, 318, 402, 317, 14 (lower center), 87, 178, 88, 95
                    const innerLipIndices = [
                        78, 191, 80, 81, 82, 13, 312, 311, 310, 415,
                        308, 324, 318, 402, 317, 14, 87, 178, 88, 95
                    ];

                    const innerLipCoords = innerLipIndices.map(idx => ({
                        x: landmarks[idx].x * width,
                        y: landmarks[idx].y * height
                    }));

                    // Compute mouth center and dimensions
                    let minX = width, maxX = 0, minY = height, maxY = 0;
                    innerLipCoords.forEach(p => {
                        if (p.x < minX) minX = p.x;
                        if (p.x > maxX) maxX = p.x;
                        if (p.y < minY) minY = p.y;
                        if (p.y > maxY) maxY = p.y;
                    });

                    const mouthWidth = maxX - minX;
                    const mouthHeight = Math.max(maxY - minY, mouthWidth * 0.35);
                    const centerX = minX + mouthWidth / 2;
                    const centerY = minY + mouthHeight / 2;

                    // Expand crop context around mouth for diffusion continuity
                    const cropSize = Math.max(mouthWidth * 2.3, mouthHeight * 3.2, 280);
                    const halfSize = cropSize / 2;

                    cropX = Math.max(0, Math.min(centerX - halfSize, width - cropSize));
                    cropY = Math.max(0, Math.min(centerY - halfSize, height - cropSize));
                    cropWidth = Math.min(cropSize, width - cropX);
                    cropHeight = Math.min(cropSize, height - cropY);

                    // Transform inner lip coordinates into 1024x1024 crop space
                    const scaleX = 1024 / cropWidth;
                    const scaleY = 1024 / cropHeight;

                    innerLipPointsInCrop = innerLipCoords.map(p => ({
                        x: (p.x - cropX) * scaleX,
                        y: (p.y - cropY) * scaleY
                    }));
                }
            } catch (err) {
                console.warn("FaceLandmarker detection error, falling back to proportional crop:", err);
            }

            // Fallback if landmarking could not find a face
            if (!cropWidth || !cropHeight || innerLipPointsInCrop.length === 0) {
                const cropSize = Math.min(width, height) * 0.5;
                cropX = (width - cropSize) / 2;
                cropY = height * 0.45;
                cropWidth = cropSize;
                cropHeight = cropSize;
            }

            const targetResolution = 1024;

            // 1. Generate 1024x1024 Crop Canvas
            const cropImageCanvas = document.createElement('canvas');
            cropImageCanvas.width = targetResolution;
            cropImageCanvas.height = targetResolution;
            const cropImgCtx = cropImageCanvas.getContext('2d');
            cropImgCtx.drawImage(
                img,
                cropX, cropY, cropWidth, cropHeight,
                0, 0, targetResolution, targetResolution
            );
            const cropImageBase64 = cropImageCanvas.toDataURL('image/jpeg', 0.92);

            // 2. Generate 1024x1024 Pure Binary Inpainting Mask
            const cropMaskCanvas = document.createElement('canvas');
            cropMaskCanvas.width = targetResolution;
            cropMaskCanvas.height = targetResolution;
            const cropMaskCtx = cropMaskCanvas.getContext('2d');

            // Solid black background
            cropMaskCtx.fillStyle = '#000000';
            cropMaskCtx.fillRect(0, 0, targetResolution, targetResolution);

            // Draw solid white intraoral polygon precisely covering the teeth aperture and lower alveolar ridge
            cropMaskCtx.fillStyle = '#ffffff';
            cropMaskCtx.strokeStyle = '#ffffff';
            cropMaskCtx.lineWidth = 10;
            cropMaskCtx.lineJoin = 'round';
            cropMaskCtx.beginPath();

            if (innerLipPointsInCrop.length > 0) {
                // Calculate centroid of mouth aperture
                let sumX = 0, sumY = 0;
                innerLipPointsInCrop.forEach(p => { sumX += p.x; sumY += p.y; });
                const cx = sumX / innerLipPointsInCrop.length;
                const cy = sumY / innerLipPointsInCrop.length;

                // Extend lower boundary downward (15%) to guarantee the lower alveolar gum ridge is covered for edentulous cases
                const points = innerLipPointsInCrop.map(p => ({
                    x: p.x,
                    y: cy + (p.y - cy) * (p.y > cy ? 1.15 : 1.0)
                }));

                cropMaskCtx.moveTo(points[0].x, points[0].y);
                for (let i = 1; i < points.length; i++) {
                    cropMaskCtx.lineTo(points[i].x, points[i].y);
                }
                cropMaskCtx.closePath();
                cropMaskCtx.fill();
                cropMaskCtx.stroke();
            } else {
                // Proportional oval fallback
                cropMaskCtx.ellipse(512, 512, 280, 140, 0, 0, Math.PI * 2);
                cropMaskCtx.fill();
            }

            const cropMaskBase64 = cropMaskCanvas.toDataURL('image/png');

            resolve({
                cropImageBase64,
                cropMaskBase64,
                cropMetadata: {
                    cropX,
                    cropY,
                    cropWidth,
                    cropHeight,
                    originalWidth: width,
                    originalHeight: height
                }
            });
        };
        img.src = sourceImageBase64;
    });
};

/* ── Seamless Full-Resolution ROI Stitcher ── */
const stitchCroppedImage = (originalImageBase64, generatedCropUrl, cropMetadata) => {
    return new Promise((resolve) => {
        if (!cropMetadata) { resolve(generatedCropUrl); return; }
        const origImg = new Image();
        const genImg = new Image();
        let loaded = 0;

        const handleLoad = () => {
            loaded++;
            if (loaded === 2) {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = cropMetadata.originalWidth;
                    canvas.height = cropMetadata.originalHeight;
                    const ctx = canvas.getContext('2d');

                    // 1. Draw 100% original full-resolution photo (preserving original eyes, skin, hair, and clothing)
                    ctx.drawImage(origImg, 0, 0, canvas.width, canvas.height);

                    // 2. Stitch the high-res generated All-on-X smile seamlessly into the original photo
                    const { cropX, cropY, cropWidth, cropHeight } = cropMetadata;
                    ctx.drawImage(genImg, cropX, cropY, cropWidth, cropHeight);

                    resolve(canvas.toDataURL('image/jpeg', 0.95));
                } catch (err) {
                    console.error('Stitch fallback:', err);
                    resolve(generatedCropUrl);
                }
            }
        };

        origImg.onload = handleLoad;
        genImg.onload = handleLoad;
        origImg.src = originalImageBase64;
        genImg.src = generatedCropUrl;
        genImg.crossOrigin = 'anonymous';
    });
};

/* ── Interactive Before/After Slider ── */
const ImageSlider = ({ before, after, onDownload }) => {
    const [position, setPosition] = useState(50);
    const sliderRef = useRef(null);

    const handleMove = (clientX) => {
        if (!sliderRef.current) return;
        const rect = sliderRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
        setPosition(percent);
    };

    return (
        <div>
            <div
                ref={sliderRef}
                style={{
                    position: 'relative', width: '100%', maxWidth: '450px', aspectRatio: '3/4',
                    overflow: 'hidden', borderRadius: '20px', cursor: 'ew-resize',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', margin: '0 auto',
                    backgroundColor: 'var(--gray-100)'
                }}
                onMouseMove={e => handleMove(e.clientX)}
                onTouchMove={e => handleMove(e.touches[0].clientX)}
            >
                {/* After Image (Background) */}
                <img src={after} alt="After Preview" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />

                {/* Before Image (Foreground, clipped) */}
                <img
                    src={before}
                    alt="Before"
                    style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
                        clipPath: `inset(0 ${100 - position}% 0 0)`
                    }}
                    draggable={false}
                />

                {/* Slider Handle */}
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${position}%`, width: '4px', backgroundColor: 'white', transform: 'translateX(-50%)', boxShadow: '0 0 15px rgba(0,0,0,0.4)' }}>
                    <div style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        width: '36px', height: '36px', backgroundColor: 'white', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                            <ChevronLeft size={14} color="#111" />
                            <ChevronRight size={14} color="#111" />
                        </div>
                    </div>
                </div>

                {/* Badges */}
                <div style={{ position: 'absolute', top: '20px', left: '20px', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', padding: '6px 14px', borderRadius: '24px', fontSize: '0.85rem', fontWeight: 600, backdropFilter: 'blur(8px)', letterSpacing: '0.5px' }}>Before</div>
                <div style={{ position: 'absolute', top: '20px', right: '20px', background: 'linear-gradient(135deg, var(--teal) 0%, var(--navy) 100%)', color: 'white', padding: '6px 14px', borderRadius: '24px', fontSize: '0.85rem', fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', letterSpacing: '0.5px' }}>After Preview</div>

                <div style={{ position: 'absolute', bottom: '20px', left: '0', width: '100%', textAlign: 'center', color: 'white', fontSize: '0.8rem', opacity: 0.8, textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                    Drag slider to compare
                </div>
            </div>

            {/* Quick download action */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '14px' }}>
                <button
                    onClick={onDownload}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-muted)',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        transition: 'color 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.color = 'var(--teal)'}
                    onMouseOut={e => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                    <Download size={15} /> Download High-Res Photo
                </button>
            </div>
        </div>
    );
};

export default function SmilePreview({ isEmbed = false }) {
    const [searchParams] = useSearchParams();
    const embedMode = isEmbed || searchParams.get('embed') === 'true' || searchParams.get('isEmbed') === 'true';
    const ctaUrl = searchParams.get('cta_url') || 'https://titanium-surgicalarts.com';
    const ctaText = searchParams.get('cta_text') || 'Book Consultation';
    const themeParam = searchParams.get('theme');

    const [darkMode, setDarkMode] = useState(themeParam === 'dark');
    const [selectedImage, setSelectedImage] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [agreed, setAgreed] = useState(true);

    // Aesthetic Shade Selection
    const [shade, setShade] = useState('natural_pearl'); // 'natural_pearl' | 'hollywood' | 'warm_natural'

    // Cached detection result
    const [cropData, setCropData] = useState(null);

    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const rootContainerRef = useRef(null);

    // Preload MediaPipe model in background on page load
    useEffect(() => {
        getFaceLandmarker().catch(err => console.warn("Background MediaPipe preload error:", err));
    }, []);

    // Auto-sync iframe height with Webflow parent container via postMessage
    useEffect(() => {
        if (!embedMode) return;

        const notifyHeight = () => {
            const el = rootContainerRef.current || document.body;
            const height = el.scrollHeight || document.documentElement.scrollHeight;
            window.parent?.postMessage({
                type: 'SMILE_PREVIEW_RESIZE',
                height: Math.ceil(height),
                step: isGenerating ? 'generating' : generatedImage ? 'result' : selectedImage ? 'preview' : 'upload'
            }, '*');
        };

        notifyHeight();
        const t1 = setTimeout(notifyHeight, 100);
        const t2 = setTimeout(notifyHeight, 300);
        const t3 = setTimeout(notifyHeight, 800);

        let resizeObserver;
        try {
            resizeObserver = new ResizeObserver(() => notifyHeight());
            if (rootContainerRef.current) {
                resizeObserver.observe(rootContainerRef.current);
            }
        } catch (e) {
            // Fallback
        }
        window.addEventListener('resize', notifyHeight);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            if (resizeObserver) resizeObserver.disconnect();
            window.removeEventListener('resize', notifyHeight);
        };
    }, [embedMode, isGenerating, generatedImage, selectedImage, error, isAnalyzing]);

    const compressImage = (file, maxWidth = 1200) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const scaleSize = maxWidth / img.width;
                    const width = img.width > maxWidth ? maxWidth : img.width;
                    const height = img.width > maxWidth ? img.height * scaleSize : img.height;

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                    resolve(dataUrl);
                };
                img.onerror = (err) => reject(err);
            };
            reader.onerror = (err) => reject(err);
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                setError(null);
                setIsAnalyzing(true);
                const compressedBase64 = await compressImage(file, 1200);
                setSelectedImage(compressedBase64);
                setGeneratedImage(null);

                // Run automatic landmark detection
                const detected = await autoDetectMouthAndMask(compressedBase64);
                setCropData(detected);
            } catch (err) {
                console.error("Image processing error:", err);
                setError("Failed to process the photo. Please try uploading another photo.");
            } finally {
                setIsAnalyzing(false);
            }
        }
    };

    const handleGenerate = async () => {
        if (!selectedImage) return;

        setIsGenerating(true);
        setError(null);

        try {
            // If crop data is not ready yet, compute it now
            let activeCrop = cropData;
            if (!activeCrop) {
                activeCrop = await autoDetectMouthAndMask(selectedImage);
                setCropData(activeCrop);
            }

            const res = await fetch('/api/replicate/smile', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imageBase64: activeCrop.cropImageBase64,
                    maskBase64: activeCrop.cropMaskBase64,
                    shade,
                    cropMetadata: activeCrop.cropMetadata
                })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to generate smile preview.');

            // Seamlessly composite the high-res generated mouth back into the full-res photo
            const finalStitchedUrl = await stitchCroppedImage(
                selectedImage,
                data.imageUrl,
                data.cropMetadata
            );

            setGeneratedImage(finalStitchedUrl);

            // Notify parent Webflow page on successful generation
            if (embedMode) {
                window.parent?.postMessage({
                    type: 'SMILE_PREVIEW_GENERATED',
                    imageUrl: finalStitchedUrl
                }, '*');
            }
        } catch (err) {
            setError(err.message || 'Generation failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownload = () => {
        if (!generatedImage) return;
        const link = document.createElement('a');
        link.href = generatedImage;
        link.download = `titanium-smile-preview-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCtaClick = () => {
        if (embedMode) {
            window.parent?.postMessage({
                type: 'SMILE_PREVIEW_CTA_CLICK',
                action: 'book_consultation',
                imageUrl: generatedImage
            }, '*');
        }
        if (ctaUrl) {
            window.open(ctaUrl, '_top');
        }
    };

    return (
        <div
            ref={rootContainerRef}
            className={`app${darkMode ? ' dark' : ''}`}
            style={{
                minHeight: embedMode ? 'auto' : '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: embedMode ? 'transparent' : undefined
            }}
        >
            <style>{`
            @keyframes slideUpFade {
                0% { opacity: 0; transform: translateY(20px); }
                100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes pulseGlow {
                0% { box-shadow: 0 0 0 0 rgba(0, 168, 181, 0.4); }
                70% { box-shadow: 0 0 0 15px rgba(0, 168, 181, 0); }
                100% { box-shadow: 0 0 0 0 rgba(0, 168, 181, 0); }
            }
            @keyframes textShimmer {
                0% { background-position: -200% center; }
                100% { background-position: 200% center; }
            }
            .animate-step { animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
            .pulse-loader { animation: pulseGlow 2s infinite; }
            .shimmer-text {
                background: linear-gradient(90deg, #fff 0%, #aaa 50%, #fff 100%);
                background-size: 200% auto;
                color: transparent;
                -webkit-background-clip: text;
                animation: textShimmer 2s linear infinite;
            }
            `}</style>

            {/* Theme Toggle - Standalone full page view */}
            {!embedMode && (
                <button
                    className="trf-theme-toggle"
                    onClick={() => setDarkMode(d => !d)}
                    aria-label="Toggle dark mode"
                    style={{ top: '24px', right: '24px', zIndex: 100 }}
                >
                    {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>
            )}

            {/* Header - Standalone full page view */}
            {!embedMode && (
                <header className="trf-header" style={{ paddingBottom: '3rem' }}>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div className="trf-header-logo-text">
                            <span style={{ color: '#ffffff' }}>Titanium</span> <span>Surgical Arts</span>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '16px', background: 'rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: '100px', color: 'white', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <Sparkles size={14} color="var(--teal)" />
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>AI Smile Studio</span>
                        </div>
                    </div>
                    <div className="trf-header-divider" />
                </header>
            )}

            {/* Main Content */}
            <main style={{ flex: 1, padding: embedMode ? '0.75rem 0.5rem' : '3rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
                <div className="trf-container" style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 2 }}>

                    {/* Back Link - Standalone only */}
                    {!embedMode && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                            <a href="https://titanium-surgicalarts.com" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color='var(--teal)'} onMouseOut={e => e.currentTarget.style.color='var(--text-muted)'}>
                                <ArrowLeft size={16} /> Return to Homepage
                            </a>
                        </div>
                    )}

                    <div className="trf-card" style={{ padding: embedMode ? 'clamp(1.25rem, 4vw, 2.5rem) clamp(0.75rem, 3vw, 1.75rem)' : 'clamp(1.5rem, 5vw, 3.5rem) clamp(1rem, 5vw, 2.5rem)', textAlign: 'center', borderRadius: '24px', boxShadow: embedMode ? '0 10px 30px rgba(0,0,0,0.05)' : '0 20px 40px rgba(0,0,0,0.06)' }}>

                        {/* Embed Badge */}
                        {embedMode && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '1rem', background: 'rgba(0, 168, 181, 0.1)', padding: '6px 14px', borderRadius: '100px', color: 'var(--teal)', border: '1px solid rgba(0, 168, 181, 0.2)' }}>
                                <Sparkles size={14} />
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Titanium AI Smile Studio</span>
                            </div>
                        )}

                        <h1 className="animate-step" style={{ fontSize: embedMode ? 'clamp(1.35rem, 4.5vw, 1.9rem)' : 'clamp(1.6rem, 5vw, 2.2rem)', fontWeight: 800, color: 'var(--text)', marginBottom: '0.75rem', letterSpacing: '-0.5px' }}>
                            Visualize Your Future Smile
                        </h1>
                        <p style={{ color: 'var(--text-muted)', fontSize: embedMode ? '0.95rem' : '1.1rem', maxWidth: '550px', margin: '0 auto 2rem auto', lineHeight: 1.5 }}>
                            Upload a photo to automatically preview your straight All-on-X smile transformation with 100% facial identity preservation.
                        </p>

                        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />
                        <input type="file" accept="image/*" capture="user" ref={cameraInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />

                        {error && (
                            <div style={{ marginBottom: '2rem', padding: '16px', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '12px', border: '1px solid #f87171', fontWeight: 500 }}>
                                {error}
                            </div>
                        )}

                        {/* STEP 1: Upload View */}
                        {!selectedImage && (
                            <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                                <div style={{ background: 'var(--surface)', borderRadius: '20px', padding: '18px 20px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', maxWidth: '580px', margin: '0 auto 2rem auto' }}>
                                    <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                                        <div style={{ background: 'var(--blue-light, #e0f2fe)', padding: '10px', borderRadius: '14px' }}>
                                            <ShieldCheck size={24} color="var(--teal)" />
                                        </div>
                                        <div style={{ textAlign: 'left' }}>
                                            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 4px 0', color: 'var(--text)' }}>1-Click Automatic Smile Preview</h2>
                                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                                Upload or snap a selfie. Our clinical AI automatically identifies your smile line and generates your preview instantly.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.25rem', maxWidth: '540px', margin: '0 auto' }}>
                                    <button
                                        onClick={() => cameraInputRef.current?.click()}
                                        style={{ background: 'var(--gray-100)', border: '2px dashed var(--gray-200)', padding: '2rem 1rem', borderRadius: '18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', cursor: 'pointer', transition: 'all 0.2s', color: 'var(--text)' }}
                                        onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.backgroundColor = 'var(--teal-light)'; }}
                                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.backgroundColor = 'var(--gray-100)'; }}
                                    >
                                        <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                            <Camera size={26} color="var(--teal)" />
                                        </div>
                                        <span style={{ fontSize: '1rem', fontWeight: 700 }}>Take a Selfie</span>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Use camera</span>
                                    </button>

                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ background: 'linear-gradient(135deg, var(--teal) 0%, var(--navy) 100%)', border: 'none', padding: '2rem 1rem', borderRadius: '18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', color: 'white', boxShadow: '0 10px 25px rgba(0, 168, 181, 0.3)' }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 168, 181, 0.4)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 168, 181, 0.3)' }}
                                    >
                                        <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(5px)' }}>
                                            <Upload size={26} color="white" />
                                        </div>
                                        <span style={{ fontSize: '1rem', fontWeight: 700 }}>Upload Photo</span>
                                        <span style={{ fontSize: '0.8rem', opacity: 0.85 }}>From device gallery</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* STEP 2: 1-Click Shade Selection & Confirmation / Results */}
                        {selectedImage && (
                            <div style={{ animation: 'fadeIn 0.5s ease-out' }}>

                                {!generatedImage ? (
                                    <div>
                                        {/* Image Display & Loading Overlay */}
                                        <div style={{ position: 'relative', width: '100%', maxWidth: '420px', aspectRatio: '3/4', margin: '0 auto 1.5rem auto', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
                                            <img src={selectedImage} alt="Uploaded portrait" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

                                            {/* AI Face Detection Badge */}
                                            <div style={{ position: 'absolute', top: '14px', left: '14px', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', padding: '6px 12px', borderRadius: '20px', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 600 }}>
                                                <CheckCircle2 size={14} color="var(--teal)" />
                                                <span>Face & Smile Detected</span>
                                            </div>

                                            {/* Shimmer Generating State */}
                                            {isGenerating && (
                                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', padding: '20px' }}>
                                                    <div style={{ position: 'relative', width: '64px', height: '64px', marginBottom: '20px' }}>
                                                        <div className="pulse-loader" style={{ position: 'absolute', inset: 0, border: '3px solid rgba(255,255,255,0.2)', borderRadius: '50%' }}></div>
                                                        <div style={{ position: 'absolute', inset: 0, border: '3px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                                    </div>
                                                    <h3 className="shimmer-text" style={{ fontSize: '1.25rem', margin: '0 0 8px 0', fontWeight: 700 }}>Architecting All-on-X Smile...</h3>
                                                    <p style={{ opacity: 0.85, fontSize: '0.85rem', margin: 0, lineHeight: 1.4, maxWidth: '280px' }}>
                                                        Aligning upper & lower teeth with {shade === 'hollywood' ? 'Hollywood Bleach' : shade === 'warm_natural' ? 'Warm Natural' : 'Natural Pearl'} shade.
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Shade Selection Selector */}
                                        {!isGenerating && (
                                            <div style={{ marginBottom: '1.5rem', background: 'var(--surface)', padding: '16px 20px', borderRadius: '18px', border: '1px solid var(--border)', maxWidth: '520px', margin: '0 auto 1.5rem auto', textAlign: 'left' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Select Desired Tooth Shade
                                                    </label>
                                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--teal)', background: 'rgba(0,168,181,0.1)', padding: '3px 8px', borderRadius: '6px' }}>
                                                        All-on-X Restoration
                                                    </span>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                                                    {[
                                                        { id: 'natural_pearl', label: 'Natural Pearl', desc: 'Natural soft white' },
                                                        { id: 'hollywood', label: 'Hollywood', desc: 'Bright radiant' },
                                                        { id: 'warm_natural', label: 'Warm Natural', desc: 'Warm ivory' }
                                                    ].map(s => (
                                                        <button
                                                            key={s.id}
                                                            onClick={() => setShade(s.id)}
                                                            style={{
                                                                padding: '10px 8px',
                                                                borderRadius: '12px',
                                                                border: shade === s.id ? '2px solid var(--teal)' : '1px solid var(--border)',
                                                                background: shade === s.id ? 'rgba(0, 168, 181, 0.1)' : 'transparent',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                textAlign: 'center',
                                                                color: 'var(--text)'
                                                            }}
                                                        >
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{s.label}</div>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>{s.desc}</div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        {!isGenerating && (
                                            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '520px', margin: '0 auto' }}>
                                                <button
                                                    className="trf-btn-secondary"
                                                    onClick={() => { setSelectedImage(null); setCropData(null); }}
                                                    style={{ flex: '1 1 35%', padding: '14px 18px', fontSize: '0.95rem', borderRadius: '12px' }}
                                                >
                                                    Change Photo
                                                </button>

                                                <button
                                                    className="trf-btn-submit"
                                                    onClick={handleGenerate}
                                                    disabled={isAnalyzing}
                                                    style={{
                                                        flex: '1 1 60%',
                                                        margin: 0,
                                                        padding: '14px 24px',
                                                        borderRadius: '12px',
                                                        background: 'linear-gradient(135deg, var(--teal) 0%, var(--navy) 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        fontWeight: 700,
                                                        fontSize: '1.05rem',
                                                        boxShadow: '0 10px 25px rgba(0, 168, 181, 0.3)',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <Sparkles size={18} /> Generate AI Smile Preview
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* STEP 3: Results View with Interactive Slider */
                                    <div>
                                        <ImageSlider before={selectedImage} after={generatedImage} onDownload={handleDownload} />

                                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button
                                                className="trf-btn-secondary"
                                                onClick={() => { setSelectedImage(null); setGeneratedImage(null); setCropData(null); }}
                                                style={{ padding: '12px 24px', fontSize: '0.95rem', borderRadius: '12px', flex: '1 1 140px', maxWidth: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                            >
                                                <RefreshCw size={16} /> Try Another Photo
                                            </button>

                                            <button
                                                className="trf-btn-submit"
                                                onClick={handleCtaClick}
                                                style={{
                                                    margin: 0,
                                                    padding: '12px 24px',
                                                    borderRadius: '12px',
                                                    background: 'linear-gradient(135deg, var(--teal) 0%, var(--navy) 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    fontWeight: 700,
                                                    fontSize: '0.95rem',
                                                    boxShadow: '0 10px 25px rgba(0, 168, 181, 0.3)',
                                                    cursor: 'pointer',
                                                    flex: '1 1 200px',
                                                    maxWidth: '260px'
                                                }}
                                            >
                                                <Sparkles size={18} /> {ctaText}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
